import { Component } from '@theme/component';

/**
 * Pure UI controller for the NINKLES buy box (blocks/buy-box.liquid).
 *
 * This element only owns the offer/frequency/pack radios. It does not submit the
 * cart itself: it reaches out to the surrounding `<product-form-component>` form
 * (already provided by Horizon) and updates the hidden `id` / `selling_plan`
 * inputs, the Add to Bag label, the price/compare-at display, and the
 * illustrative patches/nights usage line, then leaves the real submit/cart-drawer
 * flow to Horizon's existing `product-form.js` so this stays a thin selection
 * layer rather than a second cart implementation.
 * Each radio's `data-*` attributes describe a real Shopify variant (the page's
 * own 1-pack variant, or a separate real 2-pack/3-pack bundle product picked
 * in the block settings) - quantity always stays 1, since a "pack" here means
 * one unit of that specific variant, not a multiple of the base product.
 *
 * 1/2/3 Pack are three states of one real product each, so picking a pack
 * pill can mean switching to an entirely different Shopify product, not just
 * a variant within one product. Whenever that happens, #applyPackState()
 * pushes the same real product data (from blocks/buy-box.liquid's
 * `data-pack-states` JSON - never recalculated or guessed client-side) to
 * every other part of the page that must agree with the buy box: the hero
 * image, the H1 title, the patch-count summary line, and the native price
 * blocks outside the buy box (main product-details price, sticky add-to-cart
 * bar). This keeps "which pack is selected" a single source of truth instead
 * of a collection of independently-updated DOM patches.
 */
// The H1 title and top summary line are native `text` blocks (product.json
// blocks "text_xpVMaM" / "text_ninkles_value_line") using richtext settings -
// Shopify's richtext sanitizer strips custom data-* attributes from that
// content, so a `data-*` hook isn't available inside it. Horizon's own text
// snippet stamps each block's wrapper with a `text-block--{block.id}` class
// (snippets/text.liquid), where block.id is rendered as an opaque
// per-template prefix plus the literal block key ("text-block--
// ASTU2R0cxSHNqOEMwc__text_xpVMaM") - stable across reloads, but the prefix
// itself isn't something to hardcode, so match on the literal key suffix
// instead, which is exactly as stable an id as a data-* hook would have been.
const TITLE_SELECTOR = '[class*="__text_xpVMaM"] h1';
const SUMMARY_SELECTOR = '[class*="__text_ninkles_value_line"] p';

export class BuyBoxComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#syncFromSelection();
    this.#correctInitialSummary();
  }

  /**
   * Fired via the theme's declarative `on:change="/handleChange"` attribute on
   * every offer, frequency and pack radio input.
   */
  handleChange() {
    this.#syncFromSelection();
  }

  /** @returns {HTMLElement | null} */
  #getWrapper() {
    return this.closest('#ninkles-buy-box') || document.getElementById('ninkles-buy-box');
  }

  /** @returns {Record<string, any> | null} */
  #getPackStates() {
    const script = this.#getWrapper()?.querySelector('script[data-pack-states]');
    if (!(script instanceof HTMLScriptElement) || !script.textContent) return null;
    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.warn('[buy-box] Could not parse pack state data:', error);
      return null;
    }
  }

  /**
   * The top patch-count summary ("240 patches · 3 targeted shapes...") is a
   * native Shopify `text` block using a dynamic-source binding, which only
   * supports simple property access - it cannot itself branch on which real
   * pack product the page belongs to, so it always renders the 1-pack figure
   * server-side. Correct it once on connect to the real figure for whichever
   * pack this page actually is - a near-instant, imperceptible fix, not a
   * pack switch.
   */
  #correctInitialSummary() {
    const wrapper = this.#getWrapper();
    const pack = wrapper?.dataset.currentPack || '1';
    const state = this.#getPackStates()?.[pack];
    if (!state) return;

    const summaryEl = document.querySelector(SUMMARY_SELECTOR);
    if (summaryEl) summaryEl.textContent = state.summary;
  }

  #syncFromSelection() {
    const form = this.closest('form');
    if (!form) return;

    const idInput = form.querySelector('[name="id"]');
    const sellingPlanInput = form.querySelector('[name="selling_plan"]');
    const label = form.querySelector('[data-buy-box-label]');
    const addToCartButton = form.querySelector('[ref="addToCartButton"]');
    const priceDisplay = form.querySelector('[data-buy-box-price-display]');
    const compareDisplay = form.querySelector('[data-buy-box-compare-display]');
    const usagePrimary = form.querySelector('[data-buy-box-usage-primary]');
    const usageSecondary = form.querySelector('[data-buy-box-usage-secondary]');

    const offer = this.querySelector('input[name^="ninkles_offer"]:checked')?.value;
    const isSubscription = offer === 'subscription';

    /** @type {HTMLElement | null} */
    let selected = null;

    if (isSubscription) {
      selected = this.querySelector('input[name^="ninkles_frequency"]:checked');
    } else {
      selected = this.querySelector('input[name^="ninkles_pack"]:checked');
    }

    if (!selected) return;

    const variantId = selected.dataset.variantId || '';
    const price = selected.dataset.price || '';
    const compareAt = selected.dataset.compareAt || '';
    const nightsLine = selected.dataset.nightsLine || '';
    const perNightLine = selected.dataset.perNightLine || '';
    const available = selected.dataset.available !== 'false';
    const sellingPlanId = isSubscription ? selected.dataset.sellingPlanId || '' : '';

    if (idInput instanceof HTMLInputElement && variantId) {
      idInput.value = variantId;
    }

    if (sellingPlanInput instanceof HTMLInputElement) {
      sellingPlanInput.value = sellingPlanId;
    }

    if (label && price) {
      label.textContent = available ? `Add to Bag · ${price}` : 'Sold out';
    }

    if (priceDisplay && price) {
      priceDisplay.textContent = price;
    }

    if (compareDisplay) {
      if (compareAt) {
        compareDisplay.textContent = compareAt;
        compareDisplay.hidden = false;
      } else {
        compareDisplay.hidden = true;
      }
    }

    if (usagePrimary && nightsLine) {
      usagePrimary.textContent = nightsLine;
    }

    if (usageSecondary && perNightLine) {
      usageSecondary.textContent = perNightLine;
    }

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !available;
    }

    // A genuine pack pill (as opposed to a subscription frequency pill) means
    // the shopper just switched to a different real Shopify product - only
    // then does the rest of the page need to change to match.
    if (!isSubscription && selected.name.startsWith('ninkles_pack')) {
      this.#applyPackState(selected.value);
    }
  }

  /**
   * Applies one pack's full real state to every part of the page that must
   * agree with the buy box selection, so no two parts of the page can ever
   * describe a different pack.
   * @param {string} pack - "1", "2" or "3"
   */
  #applyPackState(pack) {
    const state = this.#getPackStates()?.[pack];
    if (!state) return;

    this.#updateHeroImage(state);
    this.#updateTitle(state);
    this.#updateSummary(state);
    this.#updateNativePriceDisplays(state);
    this.#updateUrl(state);

    const wrapper = this.#getWrapper();
    if (wrapper) wrapper.dataset.currentPack = String(state.pack);
  }

  /**
   * Swaps the real hero image (and its zoom-dialog copy) to the selected
   * pack's own real featured media - never a fake/duplicated asset. Scoped to
   * the page's single `<media-gallery>` element so this can never touch
   * unrelated imagery elsewhere on the page (recommendation cards, editorial
   * sections, etc. use different components entirely).
   * @param {any} state
   */
  #updateHeroImage(state) {
    const gallery = document.querySelector('media-gallery');
    if (!gallery) return;

    gallery.querySelectorAll('img.product-media__image').forEach((img) => {
      if (!(img instanceof HTMLImageElement)) return;
      img.src = state.imgSrc;
      if (state.imgSrcset) img.srcset = state.imgSrcset;
      img.alt = state.imgAlt;
      if (state.imgMaxRes) img.setAttribute('data_max_resolution', state.imgMaxRes);
    });

    gallery.querySelectorAll('.product-media[data-media-id]').forEach((container) => {
      if (!(container instanceof HTMLElement)) return;
      container.dataset.mediaId = state.mediaId;
      if (state.mediaRatio) container.style.setProperty('--ratio', state.mediaRatio);
    });
  }

  /** @param {any} state */
  #updateTitle(state) {
    const titleEl = document.querySelector(TITLE_SELECTOR);
    if (titleEl) titleEl.textContent = state.title;
  }

  /** @param {any} state */
  #updateSummary(state) {
    const summaryEl = document.querySelector(SUMMARY_SELECTOR);
    if (summaryEl) summaryEl.textContent = state.summary;
  }

  /**
   * Updates the two native Shopify price blocks that live outside the buy
   * box (the main product-details price block, and the sticky add-to-cart
   * bar's price) so they always agree with the buy box's own price display.
   * @param {any} state
   */
  #updateNativePriceDisplays(state) {
    document.querySelectorAll('product-price').forEach((el) => {
      this.#setPriceSpans(el, state);
    });

    const stickyBar = document.querySelector('sticky-add-to-cart');
    if (!(stickyBar instanceof HTMLElement)) return;

    const stickyPrice = stickyBar.querySelector('.sticky-add-to-cart__price');
    if (stickyPrice) this.#setPriceSpans(stickyPrice, state);

    stickyBar.dataset.currentVariantId = state.variantId;
    stickyBar.dataset.variantAvailable = String(state.available);

    const stickyTitle = stickyBar.querySelector('.sticky-add-to-cart__title');
    if (stickyTitle) stickyTitle.textContent = state.title;

    const stickyImage = stickyBar.querySelector('[ref="productImage"]');
    if (stickyImage instanceof HTMLImageElement) {
      stickyImage.src = state.imgSrc;
      stickyImage.alt = state.imgAlt;
    }

    const stickyButton = stickyBar.querySelector('[ref="addToCartButton"]');
    if (stickyButton instanceof HTMLButtonElement) {
      stickyButton.disabled = !state.available;
    }
  }

  /**
   * Updates a native `price` block's sale/compare-at spans in place, leaving
   * their visually-hidden accessibility labels untouched - the real price
   * text is always the span's last text node (see snippets/price.liquid).
   * @param {Element} scope
   * @param {any} state
   */
  #setPriceSpans(scope, state) {
    const saleEl = scope.querySelector('.price');
    if (saleEl?.lastChild && state.price) {
      saleEl.lastChild.textContent = state.price;
    }

    const compareEl = scope.querySelector('.compare-at-price');
    if (compareEl?.lastChild && state.compareAt) {
      compareEl.lastChild.textContent = state.compareAt;
    }
  }

  /**
   * Best-effort URL sync to the selected pack's real product URL, using
   * History API only (never triggers a navigation/reload). Never allowed to
   * break the actual pack switch - any failure here is swallowed.
   * @param {any} state
   */
  #updateUrl(state) {
    if (!state.productUrl) return;
    try {
      const url = new URL(state.productUrl, window.location.origin);
      if (url.pathname === window.location.pathname) return;
      window.history.replaceState(window.history.state, '', url.pathname);
    } catch (error) {
      // Visual and cart correctness matter more than the address bar - ignore.
    }
  }
}

if (!customElements.get('buy-box-component')) {
  customElements.define('buy-box-component', BuyBoxComponent);
}
